import React, { useEffect, useMemo, useState } from 'react';
import { useTokenLogin } from './use-token-login';
import { Button } from '@mm/components';
import { LoginType } from '@mm/state/controllers/SessionController';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import {
  SignUpForm,
  SignUpFormPayload,
} from '../LoginRouter/components/SignUpForm/SignUpForm';
import { LoginLayout } from '../LoginRouter/components/LoginLayout/LoginLayout';
import { useSelector } from 'react-redux';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { selectSessionSelector } from '@mm/state/selectors/userSelector';
import { useCompanyCRUDActions } from '@mm/state/actions/CompanyCRUDActions';
import { OnboardingSignupStep } from '@mm/state/model/CompanyUsersModel';

import cssApp from './app.module.css';
import cssAuthRouter from '../LoginRouter/AuthRouter.module.css';
import { ButtonColor } from '../../common/buttons/PrimaryButton/PrimaryButton';
import { CHATBOT_EDITOR } from '../../common/constants/routes';
import { Routes } from '../../routes/top-level-routes';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useAttributeCRUDActions } from '@mm/state/actions/AttributeCRUDActions';
import { useSessionCRUDActions } from '@mm/state/actions/SessionCRUDActions';
import { removeShopifyMark } from './helpers';
import { shopifyRoutes } from './constants';

export const ShopifyLogin = () => {
  const login = useTokenLogin();

  const queryString = new URLSearchParams(window.location.search);
  const accessToken = queryString.get('access_token');

  useEffect(() => {
    if (accessToken) {
      login.login(accessToken);
    }
  }, [accessToken]);

  return (
    <div className={cssApp.container}>
      <div className={cssApp.content}>
        {!accessToken && (
          <span className={cssApp.message}>
            Failed to start: missing login token
          </span>
        )}
        {login.isLoginInProgress && (
          <span className={cssApp.message}>Login in progress...</span>
        )}
        {login.err && (
          <>
            <span className={cssApp.message}>Failed to start application</span>
            {console.log('err', login.err)}
            <span className={cssApp.error}>{login.err?.toString?.()}</span>
          </>
        )}

        {(login.err || !accessToken) && (
          <Button color={ButtonColor.blue} onClick={() => login.logout()}>
            Go to Shopify Admin
          </Button>
        )}
      </div>
    </div>
  );
};

export const ShopifyLogout = () => {
  const login = useTokenLogin();

  useEffect(() => {
    login.logout();
  }, []);
  return (
    <div className={cssApp.container}>
      <div className={cssApp.content}>
        <span className={cssApp.message}>Log out...</span>
      </div>
    </div>
  );
};

export const ShopifyReset = () => {
  const history = useHistory();
  useEffect(() => {
    removeShopifyMark();
    history.push('/');
  }, []);

  return (
    <div className={cssApp.container}>
      <div className={cssApp.content}>
        <span className={cssApp.message}>Clearing up...</span>
      </div>
    </div>
  );
};

export const ShopifySignUpForm = () => {
  const usersCRUDActions = useUsersCRUDActions();

  const activeCompany = useSelector(activeCompanySelector);
  const [busy, setBusy] = useState(false);
  const { id: userId, userAttributes } = useSelector(selectSessionSelector);

  const history = useHistory();

  const { update } = useCompanyCRUDActions();

  const handleSubmit = async (form: SignUpFormPayload) => {
    if (busy) {
      return;
    }

    if (activeCompany) {
      const { id } = activeCompany;
      setBusy(true);
      await update({
        id,
        name: form.companyName,
        businessEmail: form.email,
        website: form.website,
        phoneNumber: form.phone,
      });

      // We do not differentiate users so far, so every shopify store user operates under global customers.ai account
      // await usersCRUDActions.updateById(id, userId, {
      //   firstName: form.firstName,
      //   lastName: form.lastName,
      // });

      setBusy(false);

      await usersCRUDActions.updateUserAttributes(id, userId, {
        smsConsent: form.smsConsent,
        onboardingSignupStep: OnboardingSignupStep.NONE,
      });

      history.push(CHATBOT_EDITOR);
    }
  };

  if (userAttributes?.onboardingSignupStep === OnboardingSignupStep.NONE) {
    return <Redirect to={CHATBOT_EDITOR} />;
  }

  return (
    <LoginLayout type={LoginType.mobile}>
      <SignUpForm
        heading=""
        aboveHeading=""
        withPhone
        withWebsite
        withCompanyName
        showFacebook={false}
        withFirstName={false}
        withLastName={false}
        withPassword={false}
        withPasswordConfirmation={false}
        forXrayOnboarding
        hideAgencyCheckbox
        css={{ form: cssAuthRouter.form }}
        onSubmit={handleSubmit}
      />
    </LoginLayout>
  );
};

export const ShopifyMain = () => {
  const session = useSelector(selectSessionSelector);
  const tokenLogin = useTokenLogin();

  const chatbotCRUDActions = useChatbotCRUDActions();
  const viewActions = useViewActions();
  const companyCRUDActions = useCompanyCRUDActions();
  const connectedPageCRUDActions = useConnectedPageCRUDActions();
  const attributeCRUDActions = useAttributeCRUDActions();
  const sessionCRUDActions = useSessionCRUDActions();

  const [err, setError] = useState();
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    (async () => {
      if (!session?.id) {
        await tokenLogin.logout();
        return;
      }

      try {
        await sessionCRUDActions.retrieveMyUser({ writeToStorage: true });
        const companiesResponse = await companyCRUDActions.retrieveAll();
        const company = companiesResponse?.companies[0];
        if (!company) {
          throw new Error('no company found');
        }
        const pages = await connectedPageCRUDActions.retrieveAllByCompany(
          company.id,
        );

        const facebookPage = pages?.facebookPages[0];
        if (!facebookPage) {
          throw new Error('no pages found');
        }
        const chatbotsResponsePromise = chatbotCRUDActions.retrieveAll(
          facebookPage.id,
        );

        await Promise.all([
          connectedPageCRUDActions.retrievePage(facebookPage.id),
          viewActions.activatePageEditor(facebookPage.id),
          attributeCRUDActions.retrieveAll(facebookPage.id),
        ]);

        const chatbot = (await chatbotsResponsePromise)?.[0];
        if (!chatbot) {
          throw new Error('no chatbot found');
        }
        await Promise.all([
          chatbotCRUDActions.setFEActiveChatbot(chatbot.id),
          viewActions.activateChatbotEditor(chatbot.id, session.id),
        ]);
      } catch (err) {
        setError(err?.toString());
      } finally {
        setBusy(false);
      }
    })();
  }, []);

  const redirectTo = useMemo(() => {
    if (
      session?.userAttributes?.onboardingSignupStep !==
      OnboardingSignupStep.NONE
    ) {
      return shopifyRoutes.signUpForm.path;
    }

    return Routes.chatbotEditor.path;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(session?.userAttributes || {})]);

  if (busy) {
    return (
      <div className={cssApp.container}>
        <div className={cssApp.content}>
          <span className={cssApp.message} />
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className={cssApp.container}>
        <div className={cssApp.content}>
          <span className={cssApp.message}>Could not start application...</span>
          <span className={cssApp.error}>{err}</span>
        </div>
      </div>
    );
  }

  return (
    <Switch>
      <Route
        key={shopifyRoutes.signUpForm.path}
        path={shopifyRoutes.signUpForm.path}
        component={shopifyRoutes.signUpForm.component}
      />
      <Route
        key={Routes.chatbotEditor.path}
        path={Routes.chatbotEditor.path}
        component={Routes.chatbotEditor.component}
      />
      <Route
        key={shopifyRoutes.billing.path}
        path={shopifyRoutes.billing.path}
        component={shopifyRoutes.billing.component}
      />
      <Redirect to={redirectTo} />
    </Switch>
  );
};

export const ShopifyApp = () => {
  return (
    <Switch>
      <Route
        key={shopifyRoutes.login.path}
        path={shopifyRoutes.login.path}
        component={shopifyRoutes.login.component}
      />
      <Route
        key={shopifyRoutes.logout.path}
        path={shopifyRoutes.logout.path}
        component={shopifyRoutes.logout.component}
      />
      <Route
        key={Routes.shopifyPublicPricingPage.path}
        path={Routes.shopifyPublicPricingPage.path}
        component={Routes.shopifyPublicPricingPage.component}
      />

      <Route key="/" path="/" component={ShopifyMain} />
    </Switch>
  );
};
